/**
 * NOTE: 年末調整（YearEndAdjustment）route-local mock の正データ・導出ビルダー。
 * バックエンド API 対応後は本ファイルごと削除する。
 */
import type { GovernmentApplicationAggregateTableRowData } from '@/features/labor-core/-components/government-application-aggregate/types';
import type { OperationPackageInstanceResponse } from '@/features/operation-instances/api';
import type { OperationStepInstance } from '@/features/operation-package-instance-profile/-type';
import type { PackageInstanceTagResponse } from '@/features/operation-packages/api';
import type {
  CountYearEndAdjustmentInstancesByTagDocumentTabsOutput,
  ListYearEndAdjustmentInstancesByTagOutput,
  ListYearEndAdjustmentInstancesByTagRowOutput,
} from '@/lib/api/orpc/contracts/labor-core-year-end-adjustment-instances';

import {
  MOCK_YEA_OPERATION_PACKAGE_ID,
  MOCK_YEA_TAG_ID,
} from './yearEndAdjustmentMockIds';

/** 確認画面 `YearEndAdjustmentDocumentProgressCell` の列順と一致させる */
export const MOCK_YEA_DOCUMENT_STEPS = [
  {
    id: 'mock-yea-step-dependent-deduction',
    key: 'dependentDeduction',
    name: '扶養',
  },
  {
    id: 'mock-yea-step-spouse-deduction',
    key: 'spouseDeduction',
    name: '配偶者',
  },
  {
    id: 'mock-yea-step-insurance-premium',
    key: 'insurancePremium',
    name: '保険料',
  },
  {
    id: 'mock-yea-step-previous-employer',
    key: 'previousEmployer',
    name: '前職・他社',
  },
  {
    id: 'mock-yea-step-housing-loan',
    key: 'housingLoan',
    name: '住宅ローン',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  key: keyof Pick<
    ListYearEndAdjustmentInstancesByTagRowOutput,
    | 'dependentDeduction'
    | 'spouseDeduction'
    | 'insurancePremium'
    | 'housingLoan'
    | 'previousEmployer'
  >;
  name: string;
}>;

export const MOCK_YEA_TOTAL_STEP_COUNT = MOCK_YEA_DOCUMENT_STEPS.length;

const MOCK_TAG_CREATED_AT = '2025-11-01T00:00:00.000Z';
const MOCK_TAG_UPDATED_AT = '2025-11-15T00:00:00.000Z';
const MOCK_YEA_ANCHOR_DATE = '2025-12-01';

export const MOCK_YEA_TAG = {
  id: MOCK_YEA_TAG_ID,
  name: '2025年 年末調整',
} as const;

export const MOCK_YEA_PACKAGE_INSTANCE_TAG = {
  id: MOCK_YEA_TAG_ID,
  name: MOCK_YEA_TAG.name,
  createdAt: MOCK_TAG_CREATED_AT,
  updatedAt: MOCK_TAG_UPDATED_AT,
} as const satisfies PackageInstanceTagResponse;

type DocumentStatus =
  ListYearEndAdjustmentInstancesByTagRowOutput['dependentDeduction'];

type StepStatus = NonNullable<
  GovernmentApplicationAggregateTableRowData['columns'][number]['stepStatus']
>;

/** 確認画面 mock の正（集約一覧はここから導出する） */
export const MOCK_YEA_CANONICAL_INSTANCES = [
  {
    operationPackageInstanceId: 'mock-yea-inst-1',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-1',
      targetName: '田中 太郎',
    },
    overallStatus: 'completed',
    dependentDeduction: 'confirmed',
    spouseDeduction: 'confirmed',
    insurancePremium: 'confirmed',
    housingLoan: 'confirmed',
    previousEmployer: 'confirmed',
    dueDate: null,
    lastUpdatedAt: '2025-11-20T10:00:00.000Z',
  },
  {
    operationPackageInstanceId: 'mock-yea-inst-2',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-2',
      targetName: '佐藤 花子',
    },
    overallStatus: 'awaitingReview',
    dependentDeduction: 'submitted',
    spouseDeduction: 'submitted',
    insurancePremium: 'submitted',
    housingLoan: 'submitted',
    previousEmployer: 'notSubmitted',
    separateHandlingDocuments: ['housingLoan', 'insurancePremium'],
    dueDate: '2025-12-01',
    lastUpdatedAt: '2025-11-19T10:00:00.000Z',
  },
  {
    operationPackageInstanceId: 'mock-yea-inst-3',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-3',
      targetName: '鈴木 次郎',
    },
    overallStatus: 'returned',
    dependentDeduction: 'returned',
    spouseDeduction: 'submitted',
    insurancePremium: 'notApplicable',
    housingLoan: 'notApplicable',
    previousEmployer: 'notSubmitted',
    dueDate: '2025-12-01',
    lastUpdatedAt: '2025-11-18T10:00:00.000Z',
  },
  {
    operationPackageInstanceId: 'mock-yea-inst-4',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-4',
      targetName: '高橋 三郎',
    },
    overallStatus: 'pendingAction',
    dependentDeduction: 'notSubmitted',
    spouseDeduction: 'notSubmitted',
    insurancePremium: 'notSubmitted',
    housingLoan: 'notApplicable',
    previousEmployer: 'notApplicable',
    dueDate: '2025-12-01',
    lastUpdatedAt: '2025-11-17T10:00:00.000Z',
  },
  {
    operationPackageInstanceId: 'mock-yea-inst-5',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-5',
      targetName: '伊藤 美咲',
    },
    overallStatus: 'returned',
    dependentDeduction: 'submitted',
    spouseDeduction: 'submitted',
    insurancePremium: 'returned',
    housingLoan: 'submitted',
    previousEmployer: 'submitted',
    dueDate: '2025-12-01',
    lastUpdatedAt: '2025-11-16T10:00:00.000Z',
  },
  {
    operationPackageInstanceId: 'mock-yea-inst-6',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-6',
      targetName: '渡辺 健',
    },
    overallStatus: 'pendingAction',
    dependentDeduction: 'submitted',
    spouseDeduction: 'notApplicable',
    insurancePremium: 'notSubmitted',
    housingLoan: 'notSubmitted',
    previousEmployer: 'notApplicable',
    dueDate: '2025-12-01',
    lastUpdatedAt: '2025-11-15T10:00:00.000Z',
  },
  {
    operationPackageInstanceId: 'mock-yea-inst-7',
    target: {
      targetType: 'Person',
      targetId: 'mock-person-7',
      targetName: '山本 さやか',
    },
    overallStatus: 'completed',
    dependentDeduction: 'confirmed',
    spouseDeduction: 'notApplicable',
    insurancePremium: 'confirmed',
    housingLoan: 'confirmed',
    previousEmployer: 'notApplicable',
    dueDate: null,
    lastUpdatedAt: '2025-11-14T10:00:00.000Z',
  },
] as const satisfies ReadonlyArray<ListYearEndAdjustmentInstancesByTagRowOutput>;

function documentStatusToStepStatus(status: DocumentStatus): StepStatus | null {
  switch (status) {
    case 'confirmed':
      return 'Completed';
    case 'submitted':
      return 'InProgress';
    case 'returned':
      return 'Abort';
    case 'notSubmitted':
      return 'NotStarted';
    case 'notApplicable':
      return null;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function countApplicableDocuments(
  row: ListYearEndAdjustmentInstancesByTagRowOutput,
): number {
  return MOCK_YEA_DOCUMENT_STEPS.filter(
    (step) => row[step.key] !== 'notApplicable',
  ).length;
}

function countConfirmedDocuments(
  row: ListYearEndAdjustmentInstancesByTagRowOutput,
): number {
  return MOCK_YEA_DOCUMENT_STEPS.filter((step) => row[step.key] === 'confirmed')
    .length;
}

function buildAggregateColumns(
  row: ListYearEndAdjustmentInstancesByTagRowOutput,
): GovernmentApplicationAggregateTableRowData['columns'] {
  return MOCK_YEA_DOCUMENT_STEPS.map((step) => {
    const documentStatus = row[step.key];
    const stepStatus = documentStatusToStepStatus(documentStatus);
    return {
      operationStepDefinitionId: step.id,
      hasIncludeFile:
        documentStatus === 'submitted' ||
        documentStatus === 'returned' ||
        documentStatus === 'confirmed',
      ...(stepStatus != null ? { stepStatus } : { stepStatus: null }),
    };
  });
}

export function buildMockYeaConfirmationInstances(): ListYearEndAdjustmentInstancesByTagOutput {
  const items: ListYearEndAdjustmentInstancesByTagRowOutput[] =
    MOCK_YEA_CANONICAL_INSTANCES.map((row) => structuredClone(row));
  const count = items.length;
  return {
    items,
    length: count,
    count,
    page: 1,
    maxPage: count > 0 ? 1 : 0,
  };
}

export function buildMockYeaDocumentTabCounts(): CountYearEndAdjustmentInstancesByTagDocumentTabsOutput {
  const items = MOCK_YEA_CANONICAL_INSTANCES;
  const countByField = (
    key: (typeof MOCK_YEA_DOCUMENT_STEPS)[number]['key'],
  ): number => items.filter((row) => row[key] !== 'notApplicable').length;

  return {
    all: items.length,
    dependentDeduction: countByField('dependentDeduction'),
    spouseDeduction: countByField('spouseDeduction'),
    insurancePremium: countByField('insurancePremium'),
    housingLoan: countByField('housingLoan'),
    previousEmployer: countByField('previousEmployer'),
  };
}

export function buildMockYeaGovernmentApplicationInstances(): {
  items: GovernmentApplicationAggregateTableRowData[];
  count: number;
  length: number;
  page: number;
  maxPage: number;
} {
  const items = MOCK_YEA_CANONICAL_INSTANCES.map((row) => {
    const columns = buildAggregateColumns(row);
    return {
      id: row.operationPackageInstanceId,
      completedOperationStepCount: countConfirmedDocuments(row),
      totalOperationStepCount: countApplicableDocuments(row),
      target: structuredClone(row.target),
      anchorDate: MOCK_YEA_ANCHOR_DATE,
      tag: structuredClone(MOCK_YEA_TAG),
      columns,
    };
  });
  const count = items.length;
  return {
    items,
    count,
    length: count,
    page: 1,
    maxPage: count > 0 ? 1 : 0,
  };
}

/** サイドバー・パターンタブ用: 未完了インスタンス数 */
export function countMockYeaInProgressInstances(): number {
  return MOCK_YEA_CANONICAL_INSTANCES.filter(
    (row) => row.overallStatus !== 'completed',
  ).length;
}

type MockTaskStatus =
  OperationStepInstance['operationTaskInstances'][number]['status'];

function documentStatusToFormTaskStatus(
  status: DocumentStatus,
): MockTaskStatus {
  switch (status) {
    case 'confirmed':
    case 'submitted':
      return 'Completed';
    case 'returned':
      return 'InProgress';
    case 'notSubmitted':
      return 'NotStarted';
    case 'notApplicable':
      return 'Skipped';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function documentStatusToConfirmationTaskStatus(
  status: DocumentStatus,
): MockTaskStatus {
  switch (status) {
    case 'confirmed':
      return 'Completed';
    case 'submitted':
      return 'InProgress';
    case 'returned':
      return 'NotStarted';
    case 'notSubmitted':
      return 'NotStarted';
    case 'notApplicable':
      return 'Skipped';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function documentStatusToOperationStepInstanceStatus(
  status: DocumentStatus,
): OperationStepInstance['status'] {
  switch (status) {
    case 'confirmed':
      return 'Completed';
    case 'submitted':
    case 'returned':
      return 'InProgress';
    case 'notSubmitted':
      return 'NotStarted';
    case 'notApplicable':
      return 'Skipped';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function createMockTaskInstance(params: {
  id: string;
  name: string;
  taskType: 'FORM' | 'CONFIRMATION';
  status: MockTaskStatus;
}): OperationStepInstance['operationTaskInstances'][number] {
  const completedTaskCount = params.status === 'Completed' ? 1 : 0;
  return {
    id: params.id,
    name: params.name,
    taskType: params.taskType,
    status: params.status,
    assignees: [],
    checklist: [],
    flow: [],
    progress: {
      totalTaskCount: 1,
      completedTaskCount,
    },
  };
}

function buildMockOperationSteps(
  instanceId: string,
  row: ListYearEndAdjustmentInstancesByTagRowOutput,
): OperationStepInstance[] {
  return MOCK_YEA_DOCUMENT_STEPS.map((step) => {
    const documentStatus = row[step.key];
    const operationTaskInstances: OperationStepInstance['operationTaskInstances'] =
      [];

    if (documentStatus !== 'notApplicable') {
      operationTaskInstances.push(
        createMockTaskInstance({
          id: `${instanceId}-${step.key}-form`,
          name: `${step.name}（入力）`,
          taskType: 'FORM',
          status: documentStatusToFormTaskStatus(documentStatus),
        }),
        createMockTaskInstance({
          id: `${instanceId}-${step.key}-confirmation`,
          name: `${step.name}（確認）`,
          taskType: 'CONFIRMATION',
          status: documentStatusToConfirmationTaskStatus(documentStatus),
        }),
      );
    }

    return {
      id: step.id,
      name: step.name,
      status: documentStatusToOperationStepInstanceStatus(documentStatus),
      operationTaskInstances,
    };
  });
}

function mapOverallStatusToInstanceStatus(
  overallStatus: ListYearEndAdjustmentInstancesByTagRowOutput['overallStatus'],
): OperationPackageInstanceResponse['status'] {
  switch (overallStatus) {
    case 'completed':
      return 'Completed';
    case 'returned':
      return 'Aborted';
    default:
      return 'InProgress';
  }
}

/** 確認ドロワー向け Operation Package Instance 詳細 mock */
export function buildMockYeaOperationPackageInstance(
  instanceId: string,
): OperationPackageInstanceResponse | undefined {
  const row = MOCK_YEA_CANONICAL_INSTANCES.find(
    (item) => item.operationPackageInstanceId === instanceId,
  );
  if (row == null) {
    return undefined;
  }

  return {
    id: instanceId,
    name: `${MOCK_YEA_TAG.name} - ${row.target.targetName}`,
    description: undefined,
    operationPackageId: MOCK_YEA_OPERATION_PACKAGE_ID,
    patternName: MOCK_YEA_TAG.name,
    packageType: 'GovernmentApplication',
    status: mapOverallStatusToInstanceStatus(row.overallStatus),
    target: {
      targetType: row.target.targetType,
      targetId: row.target.targetId,
      targetName: row.target.targetName,
    },
    operator: {
      operatorId: 'mock-yea-operator',
      operatorName: '労務担当',
    },
    operationSteps: buildMockOperationSteps(instanceId, row),
  };
}
